import React, { useEffect, useRef, useState } from "react";

import {
  FaCalendarAlt,
  FaClock,
  FaExternalLinkAlt,
  FaInfoCircle,
  FaMapMarkerAlt,
  FaMedal,
} from "react-icons/fa";

import DOMPurify from "dompurify";

import Button from "../../../components/Button/Button";

import { extractEventCategories, normalize } from "./utils/calendarUtils";

const Badge = ({ children, className = "" }) => (
  <span
    className={[
      "inline-flex items-center rounded-full px-2.5 py-1 text-xs font-semibold",
      className,
    ].join(" ")}
  >
    {children}
  </span>
);

const levelClass = (level = "") => {
  const normalizedLevel = normalize(level);

  if (normalizedLevel.includes("international")) {
    return "bg-purple-600 text-white";
  }

  if (normalizedLevel.includes("national")) {
    return "bg-red-600 text-white";
  }

  if (normalizedLevel.includes("inter")) {
    return "bg-amber-500 text-white";
  }

  if (normalizedLevel.includes("region")) {
    return "bg-indigo-600 text-white";
  }

  if (normalizedLevel.includes("departement")) {
    return "bg-emerald-600 text-white";
  }

  return "bg-slate-200 text-slate-800 dark:bg-slate-700 dark:text-slate-100";
};

function getDateLabel(start, end) {
  const startDate = start ? new Date(start) : null;

  const endDate = end ? new Date(end) : null;

  if (!startDate || Number.isNaN(startDate.getTime())) {
    return "";
  }

  const formattedStart = startDate.toLocaleDateString("fr-FR", {
    timeZone: "Europe/Paris",
  });

  if (!endDate || Number.isNaN(endDate.getTime())) {
    return formattedStart;
  }

  const formattedEnd = endDate.toLocaleDateString("fr-FR", {
    timeZone: "Europe/Paris",
  });

  if (formattedStart === formattedEnd) {
    return formattedStart;
  }

  return `${formattedStart} → ${formattedEnd}`;
}

export default function EventCard({
  event,
  onOpenDetails,
  onImageClick,
  detailed = false,
  categories = [],
}) {
  const {
    titre,
    description,
    start,
    end,
    discipline_event,
    type_event,
    level_event,
    lieu_event,
    horaire_event,
    photo_url,
    agenda_url,
    organisateur,
  } = event;

  const ageCategories =
    Array.isArray(categories) && categories.length > 0
      ? categories
      : extractEventCategories(event?.cate_event);

  const dateLabel = getDateLabel(start, end);

  const contentRef = useRef(null);

  const [expanded, setExpanded] = useState(false);

  const [needsClamp, setNeedsClamp] = useState(false);

  const [maxHeightPx, setMaxHeightPx] = useState(0);

  useEffect(() => {
    if (detailed) return undefined;

    const element = contentRef.current;

    if (!element) return undefined;

    const measure = () => {
      const computedStyle = window.getComputedStyle(element);

      const lineHeight = parseFloat(computedStyle.lineHeight || "20") || 20;

      const visibleLines = 7;
      const maximumHeight = lineHeight * visibleLines;

      setMaxHeightPx(maximumHeight);

      const previousMaxHeight = element.style.maxHeight;

      const previousOverflow = element.style.overflow;

      element.style.maxHeight = "none";
      element.style.overflow = "visible";

      const scrollHeight = element.scrollHeight;

      element.style.maxHeight = previousMaxHeight;

      element.style.overflow = previousOverflow;

      setNeedsClamp(scrollHeight > maximumHeight + 2);
    };

    const requestId = requestAnimationFrame(measure);

    let resizeObserver = null;

    if (typeof ResizeObserver !== "undefined") {
      resizeObserver = new ResizeObserver(() => {
        requestAnimationFrame(measure);
      });

      resizeObserver.observe(element);
    }

    return () => {
      cancelAnimationFrame(requestId);
      resizeObserver?.disconnect();
    };
  }, [description, detailed]);

  useEffect(() => {
    setExpanded(false);
  }, [event?.id]);

  return (
    <article className="group overflow-hidden rounded-2xl bg-white/95 shadow-xl ring-1 ring-slate-200/70 transition hover:-translate-y-0.5 hover:shadow-2xl dark:bg-slate-900/95 dark:ring-slate-800">
      <div className="h-1 w-full bg-gradient-to-r from-blue-500 via-violet-500 to-pink-500" />

      {photo_url ? (
        <div
          className={[
            "relative h-44 w-full overflow-hidden",
            detailed && onImageClick ? "cursor-zoom-in" : "",
          ].join(" ")}
          onClick={(clickEvent) => {
            if (!detailed || !onImageClick) {
              return;
            }

            clickEvent.stopPropagation();
            onImageClick();
          }}
          role={detailed && onImageClick ? "button" : undefined}
          tabIndex={detailed && onImageClick ? 0 : undefined}
          onKeyDown={(keyboardEvent) => {
            if (!detailed || !onImageClick) {
              return;
            }

            if (keyboardEvent.key === "Enter" || keyboardEvent.key === " ") {
              keyboardEvent.preventDefault();
              onImageClick();
            }
          }}
        >
          <img
            src={photo_url}
            alt={titre || "Illustration de l’événement"}
            className={[
              "h-full w-full object-cover transition duration-500",
              detailed && onImageClick
                ? "hover:scale-105"
                : "group-hover:scale-105",
            ].join(" ")}
            loading="lazy"
          />

          {detailed && onImageClick && (
            <div className="pointer-events-none absolute inset-0 flex items-center justify-center bg-black/0 transition hover:bg-black/10">
              <div className="rounded-full bg-black/60 px-3 py-1.5 text-xs font-bold text-white opacity-0 shadow-lg backdrop-blur-sm transition group-hover:opacity-100">
                Agrandir
              </div>
            </div>
          )}

          {!detailed && (
            <button
              onClick={onOpenDetails}
              title="Détails de l’événement"
              aria-label="Voir les détails de l’événement"
              className="absolute left-2 top-2 flex h-10 w-10 items-center justify-center rounded-xl bg-white/95 text-slate-600 shadow-lg transition hover:bg-white hover:text-red-600"
              type="button"
            >
              <FaInfoCircle />
            </button>
          )}

          {discipline_event && (
            <div className="absolute bottom-2 left-2">
              <Badge className="gap-1.5 bg-slate-950/75 text-white shadow-lg backdrop-blur">
                <FaMedal />
                {discipline_event}
              </Badge>
            </div>
          )}
        </div>
      ) : (
        <div className="relative flex min-h-16 w-full items-center bg-gradient-to-r from-indigo-50 to-cyan-50 px-4 dark:from-slate-800 dark:to-slate-800">
          {discipline_event && (
            <Badge className="gap-1.5 bg-blue-100 text-blue-800 dark:bg-blue-950/60 dark:text-blue-200 ml-10">
              <FaMedal />
              {discipline_event}
            </Badge>
          )}

          {!detailed && (
            <button
              onClick={onOpenDetails}
              title="Détails de l’événement"
              aria-label="Voir les détails de l’événement"
              className="absolute left-2 top-2 flex h-10 w-10 items-center justify-center rounded-xl bg-white/95 text-slate-600 shadow transition hover:bg-white hover:text-red-600"
              type="button"
            >
              <FaInfoCircle />
            </button>
          )}
        </div>
      )}

      <div className="p-4">
        <div className="flex items-start justify-between gap-3">
          <h3 className="line-clamp-3 text-lg font-bold leading-snug text-slate-900 dark:text-white">
            {titre || "Événement sans titre"}
          </h3>
        </div>

        <div className="mt-3 flex flex-wrap gap-2">
          {discipline_event && !photo_url && (
            <Badge className="gap-1.5 bg-blue-100 text-blue-800 dark:bg-blue-950/60 dark:text-blue-200">
              <FaMedal />
              {discipline_event}
            </Badge>
          )}

          {organisateur && (
            <Badge className="bg-slate-200 text-slate-800 dark:bg-slate-700 dark:text-slate-100">
              {organisateur}
            </Badge>
          )}

          {type_event && (
            <Badge className="bg-slate-100 text-slate-700 ring-1 ring-slate-200 dark:bg-slate-800 dark:text-slate-200 dark:ring-slate-700">
              {type_event}
            </Badge>
          )}

          {level_event && (
            <Badge className={levelClass(level_event)}>{level_event}</Badge>
          )}

          {ageCategories.map((category) => (
            <Badge
              key={category}
              className="bg-sky-50 text-sky-800 ring-1 ring-sky-200 dark:bg-sky-950/40 dark:text-sky-200 dark:ring-sky-900"
            >
              {category}
            </Badge>
          ))}
        </div>

        <div className="mt-4 space-y-2 text-sm text-slate-700 dark:text-slate-200">
          {dateLabel && (
            <div className="flex items-start gap-2">
              <FaCalendarAlt className="mt-0.5 shrink-0 text-red-500" />

              <span>{dateLabel}</span>
            </div>
          )}

          {horaire_event && (
            <div className="flex items-start gap-2">
              <FaClock className="mt-0.5 shrink-0 text-amber-500" />

              <span className="whitespace-pre-line">{horaire_event}</span>
            </div>
          )}

          {lieu_event && (
            <div className="flex items-start gap-2">
              <FaMapMarkerAlt className="mt-0.5 shrink-0 text-blue-500" />

              <span>{lieu_event}</span>
            </div>
          )}
        </div>

        {description && !detailed && (
          <>
            <div className="relative mt-4">
              <div
                ref={contentRef}
                className="prose prose-sm max-w-none text-sm leading-relaxed text-slate-700 transition-[max-height] duration-200 dark:prose-invert dark:text-slate-200 [&_a]:inline [&_a]:text-indigo-600 hover:[&_a]:underline dark:[&_a]:text-indigo-300"
                style={{
                  maxHeight:
                    !expanded && needsClamp && maxHeightPx
                      ? `${maxHeightPx}px`
                      : "none",

                  overflow: !expanded && needsClamp ? "hidden" : "visible",
                }}
                onClick={(clickEvent) => {
                  if (clickEvent.target?.closest?.("a")) {
                    return;
                  }

                  clickEvent.stopPropagation();
                }}
                dangerouslySetInnerHTML={{
                  __html: DOMPurify.sanitize(description),
                }}
              />

              {!expanded && needsClamp && (
                <div className="pointer-events-none absolute bottom-0 left-0 right-0 h-10 bg-gradient-to-t from-white/95 to-transparent dark:from-slate-900/95" />
              )}
            </div>

            {needsClamp && (
              <div className="mt-2">
                <Button
                  text={expanded ? "Réduire" : "Lire plus"}
                  variant="subtle"
                  className="!rounded-xl"
                  onClick={(clickEvent) => {
                    clickEvent.stopPropagation();

                    setExpanded((currentValue) => !currentValue);
                  }}
                  onMouseDown={(event) => event.stopPropagation()}
                  onPointerDown={(event) => event.stopPropagation()}
                />
              </div>
            )}
          </>
        )}

        {description && detailed && (
          <div
            className="prose prose-sm mt-4 max-w-none text-sm leading-relaxed text-slate-700 dark:prose-invert dark:text-slate-200 [&_a]:inline [&_a]:text-indigo-600 hover:[&_a]:underline dark:[&_a]:text-indigo-300"
            dangerouslySetInnerHTML={{
              __html: DOMPurify.sanitize(description),
            }}
          />
        )}

        {agenda_url && (
          <div className="mt-4 flex justify-end">
            <Button
              text="Lien PDF"
              variant="outline"
              size="sm"
              icon={FaExternalLinkAlt}
              onClick={(clickEvent) => {
                clickEvent.stopPropagation();

                window.open(agenda_url, "_blank", "noopener,noreferrer");
              }}
            />
          </div>
        )}

        {detailed && (
          <div className="mt-6 flex items-start gap-2 rounded-xl border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900 dark:border-amber-900 dark:bg-amber-950/40 dark:text-amber-200">
            <FaInfoCircle className="mt-0.5 shrink-0" />

            <span>
              Pour tout complément d’information, merci de vous reporter à
              l’Extranet de France Judo.
            </span>
          </div>
        )}
      </div>
    </article>
  );
}
